package com.sddamico.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.sddamico.SitePalette
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import com.sddamico.components.sections.Footer
import com.sddamico.components.sections.NavHeader
import com.sddamico.components.widgets.ImpersonationBanner
import com.sddamico.components.widgets.PushNotificationBanner
import com.sddamico.components.widgets.PageLoading
import com.sddamico.components.motion.PageTransitionHost

val PageContentStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(leftRight = 1.cssRem, top = 2.cssRem) }
    Breakpoint.MD { Modifier.maxWidth(780.px).padding(leftRight = 2.cssRem).styleModifier { property("margin-left", "auto"); property("margin-right", "auto") } }
    Breakpoint.LG { Modifier.maxWidth(900.px) }
}

val WidePageContentStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(leftRight = 1.cssRem, top = 2.cssRem) }
    Breakpoint.MD { Modifier.maxWidth(1100.px).padding(leftRight = 2.cssRem).styleModifier { property("margin-left", "auto"); property("margin-right", "auto") } }
    Breakpoint.LG { Modifier.maxWidth(1200.px) }
}

class PageLayoutData(val title: String, val wideContent: Boolean = false, val requiresAuth: Boolean = true)

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    val authState = LocalAuthState.current
    LaunchedEffect(data.title) {
        document.title = "FCC - ${data.title}"
    }

    // Export snapshots run signed out, so the redirect below would race the snapshot: a page was captured either as
    // this empty shell or as the sign-in page, depending on whether the 401 arrived first.
    if (data.requiresAuth && AppGlobals.isExporting) return

    // Redirect unauthenticated users to sign-in for protected pages
    if (data.requiresAuth) {
        LaunchedEffect(authState.user, authState.loading) {
            if (!authState.loading && authState.user == null) {
                ctx.router.tryRoutingTo("/sign-in")
            }
        }
    }
    val awaitingAuth = data.requiresAuth && (authState.loading || authState.user == null)

    // Query params are part of the key because pages like the clubhouse show a different club per query.
    val routeKey = ctx.route.path + ctx.route.queryParams.entries.sortedBy { it.key }.joinToString("&", prefix = "?")

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(SitePalette.background)
            .gridTemplateRows { size(1.fr); size(minContent) },
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            NavHeader()
            val contentStyle = if (data.wideContent) WidePageContentStyle else PageContentStyle
            Div(contentStyle.toAttrs()) {
                ImpersonationBanner()
                PushNotificationBanner()
                PageTransitionHost(routeKey) {
                    if (awaitingAuth) PageLoading() else content()
                }
            }
        }
        Footer(Modifier.fillMaxWidth().gridRow(2))
    }
}
